import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { SearchState } from '@cia-front-end-apps/shared/api-interaction';
import { injectQuery, injectQueryClient, queryOptions } from '@ngneat/query';
import { routeParts } from '@hq-core/routes';
import { NavBadgeService } from '@hq-core/navigation/nav-badge.service';
import { BrandResource } from './resources/brand.resource';
import { Brand } from './models/brand';
// TODO: Remove BrandDemoScenarioService import when wired to a real API.
import { BrandDemoScenarioService } from './resources/brand-demo-scenario.service';
// TODO: Remove brandsForScenario and DemoScenario imports when wired to a real API.
import { DemoScenario, brandsForScenario } from './resources/brand.mock-data';
import { UndoToastService } from './undo-toast/undo-toast.service';

@Injectable({
    providedIn: 'root',
})
export class BrandAssignmentService {
    private readonly query = injectQuery();
    private readonly queryClient = injectQueryClient();
    private readonly brandResource = inject(BrandResource);
    private readonly navBadgeService = inject(NavBadgeService);
    // TODO: Remove demoScenarioService when wired to a real API.
    private readonly demoScenarioService = inject(BrandDemoScenarioService);
    private readonly undoToastService = inject(UndoToastService);

    private readonly _pendingRemovalId = signal<number | null>(null);
    /** brandId created-but-not-yet-removed; drives the queue's leave animation on arrival. */
    readonly pendingRemovalId = this._pendingRemovalId.asReadonly();

    /**
     * Synchronous count of brands waiting for review.
     * TODO: Replace with a query-derived count when wired to a real API.
     *
     * Computed directly from the demo source of truth so the subtitle and badge update
     * the instant markRemoved/restore/setScenario is called — no async refetch hop.
     * Filter the current scenario's brands against removedIds rather than subtracting the
     * set size: markRemoved() accepts any id (e.g. a brand loaded by get() that isn't in
     * the active scenario), so subtraction could under-count or go negative.
     */
    readonly queueCount = computed(() => {
        const removed = this.demoScenarioService.removedIds();
        return brandsForScenario(this.demoScenarioService.scenario())
            .filter(brand => !removed.has(brand.brandId)).length;
    });

    constructor() {
        // Publish the queue size to NavBadgeService in sync with scenario/removedIds changes.
        effect(() => {
            this.navBadgeService.setBadge(`/${routeParts.brandAssignment}`, this.queueCount());
        });
    }

    getBrandQueueOptions(searchState: SearchState) {
        return queryOptions({
            queryKey: [
                'brandQueue',
                searchState.pageIndex,
                searchState.pageSize,
                searchState.search,
                searchState.sortBy,
                searchState.sortDirection,
            ],
            queryFn: () => this.brandResource.getAll(searchState),
            staleTime: 0,
        });
    }

    getBrandQueue(searchState: SearchState) {
        return this.query(this.getBrandQueueOptions(searchState));
    }

    getSearchParentBrandsOptions(term: string) {
        return queryOptions({
            queryKey: ['parentBrandSearch', term],
            queryFn: () => this.brandResource.searchParentBrands(term),
            staleTime: 30_000,
        });
    }

    searchParentBrands(term: string) {
        return this.query(this.getSearchParentBrandsOptions(term));
    }

    getBrandOptions(brandId: string) {
        return queryOptions({
            queryKey: ['brand', brandId],
            queryFn: () => this.brandResource.get(brandId),
            enabled: brandId !== '__none__',
            staleTime: 0,
        });
    }

    /** Loads the full detail for a single brand, including its UPCs. */
    getBrand(brandId: string) {
        return this.query(this.getBrandOptions(brandId));
    }

    /** Resets the brand-queue list cache to pending — call after demo scenario changes. */
    resetBrandQueues(): void {
        this.queryClient.resetQueries({ queryKey: ['brandQueue'] });
    }

    /**
     * Refetches the brand-queue list cache while keeping the current data visible (status
     * stays 'success'). Use for optimistic create/undo so the list never flashes to a skeleton.
     * Use resetBrandQueues() instead for scenario switches where the pending state is intentional.
     */
    refreshBrandQueues(): void {
        this.queryClient.invalidateQueries({ queryKey: ['brandQueue'] });
    }

    // TODO: Remove demoScenario, setDemoScenario, and retryQueue when wired to a real API.

    /** Exposes the current demo scenario as a read-only signal. */
    readonly demoScenario = this.demoScenarioService.scenario;

    /** Switches the demo scenario and resets the queue cache. */
    setDemoScenario(scenario: DemoScenario): void {
        this.demoScenarioService.setScenario(scenario);
        this.resetBrandQueues();
    }

    /** Resets the demo scenario to 'populated' and resets the queue cache. */
    retryQueue(): void {
        this.demoScenarioService.setScenario('populated');
        this.resetBrandQueues();
    }

    /**
     * Records a brand as pending removal, then enqueues a 30-second undo window.
     *
     * The brand is NOT immediately removed — the review queue will detect it is rendered,
     * enable animations, and call commitPendingRemoval() after a short beat so the card
     * plays its :leave animation rather than disappearing instantly on navigation.
     *
     * The actual API write is deferred until the window expires or the user dismisses
     * the toast. Undo cancels the timer, clears the pending removal, and makes no API call.
     *
     * TODO: Remove the demoScenarioService calls when wired to a real API; replace with an
     * optimistic queryClient.setQueryData update against the real server response.
     */
    createNewBrand(brand: Brand): void {
        this.enqueueRemoval(brand, `"${brand.brandName}" created as a new brand.`,
            () => this.brandResource.create(brand));
    }

    rollUpBrand(newBrand: Brand, parentBrand: Brand): void {
        this.enqueueRemoval(newBrand, `"${newBrand.brandName}" rolled up into "${parentBrand.brandName}".`,
            () => this.brandResource.rollUp(newBrand.brandId, parentBrand.brandId));
    }

    /**
     * Shared commit/undo body for both createNewBrand and rollUpBrand.
     *
     * IMPORTANT: _pendingRemovalId is set AFTER enqueue() so that the synchronous
     * supersede-commit inside enqueue() resolves the prior pending brand, not this one.
     */
    private enqueueRemoval(brand: Brand, message: string, apiCall: () => Observable<unknown>): void {
        this.undoToastService.enqueue({
            message,
            commit: () => {
                this.demoScenarioService.markRemoved(brand.brandId);
                if (this._pendingRemovalId() === brand.brandId) { this._pendingRemovalId.set(null); }
                this.refreshBrandQueues();
                // TODO: surface error to user (toast/retry) when wired to real API.
                apiCall().subscribe({ error: () => {} });
            },
            undo: () => {
                if (this._pendingRemovalId() === brand.brandId) { this._pendingRemovalId.set(null); }
                this.demoScenarioService.restore(brand.brandId);
                this.refreshBrandQueues();
            },
        });
        this._pendingRemovalId.set(brand.brandId);
    }

    /**
     * Called by the review queue after it has rendered the pending brand on-screen.
     * Idempotent — safe to call multiple times or after undo has already cleared the pending id.
     */
    commitPendingRemoval(): void {
        const id = this._pendingRemovalId();
        if (id === null) { return; }
        this._pendingRemovalId.set(null);
        this.demoScenarioService.markRemoved(id);
        this.refreshBrandQueues();
    }
}
